import CustomTable from "components/common/CustomTable";
import { formatMoney, getCurrencyFromCurrencyCode } from "helpers";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ResponsivePaginationComponent from "react-responsive-pagination";
import { salesActions } from "redux/Ldb/actions";

type SalesOrderTableProps = {
    resultMessage: {
        filters: Record<string, any>;
        orders: Array<Record<string, any>>;
        pagination: {
            current: number;
            next: number;
            number_of_pages: number;
            perPage: number;
        };
        total: number;
        summary: string;
    };
    toolName: string;
};

const SalesOrderTable = ({ resultMessage, toolName }: SalesOrderTableProps) => {
    const dispatch = useAppDispatch();
    const [page, setPage] = useState<number>(1);
    const [salesOrderList, setSalesOrderList] = useState<any[]>([]);
    const { fetchSalesOrderAgentsSuccess } = useAppSelector((state) => state.sales);

    useEffect(() => {
        dispatch(salesActions.fetchSalesOrderAgents(page, 20, resultMessage?.filters));
    }, [dispatch, page]);

    useEffect(() => {
        if (Boolean(fetchSalesOrderAgentsSuccess)) {
            setSalesOrderList(fetchSalesOrderAgentsSuccess?.sales);
        }
    }, [fetchSalesOrderAgentsSuccess, resultMessage]);


    const tableData = page === 1 ? resultMessage?.orders : salesOrderList;

    const salesOrderTableHeader = [
        { title: "Order ID", widthClass: "w-[250px] ", titleClass: "text-slate-700" },
        { title: "Product name", widthClass: "w-[150px]", titleClass: "text-slate-700" },
        { title: "Sale source", widthClass: "w-[150px]", titleClass: "text-slate-700" },
        { title: "Est. delivery date", widthClass: "w-[150px]", titleClass: "text-slate-700" },
        { title: "Est. revenue", widthClass: "w-[150px]", titleClass: "text-slate-700" },
        { title: "Status", widthClass: "w-[150px]", titleClass: "text-slate-700" },
    ];

    const salesOrderTableBody = tableData?.map((item, idx) => {
        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">SO-{item?.taId || item?.orderId || item?._id}</p>
                        <p className="text-slate-500 text-[10px]">
                            {item?.saleDate
                                ? moment(item?.saleDate)?.format("DD.MM.YYYY; H:mm a")
                                : "Not Available"}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">
                            {item?.productsOrdered
                                ?.map((item: any) => item?.productName)
                                .join(", ")}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <div className="flex items-center space-x-2 ">
                            {item?.channel?.toLowerCase()?.includes("synth")
                                ? item?.customerDetails?.company ||
                                  (item?.customerDetails?.first_name &&
                                  item?.customerDetails?.last_name
                                      ? `${item?.customerDetails?.first_name} ${item?.customerDetails?.last_name}`
                                      : item?.customerDetails?.first_name ||
                                        item?.customerDetails?.last_name) ||
                                  item?.customerDetails?.name ||
                                  ""
                                : displayProductChannelIcon(item?.channel, "!w-6 !h-6")}

                            <span className="captalize ">
                                {item?.channel?.toLowerCase()?.includes("synth")
                                    ? ""
                                    : item?.channel?.toLowerCase()?.includes("trackstar")
                                    ? item?.channel?.split(".")[1]
                                    : item?.channel}
                            </span>
                        </div>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">
                            {item?.deliveryDate
                                ? moment(item?.deliveryDate)?.format("DD.MM.YYYY")
                                : "Not Available"}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">
                            {" "}
                            {getCurrencyFromCurrencyCode(item?.currency)}
                            {formatMoney()?.format(item?.totalRevenue)}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex items-center justify-center gap-2`}
                    >
                        <p
                            className={`${
                                item?.status === "fulfilled"
                                    ? "bg-[#D0F4ED] text-g-60"
                                    : item?.status === "in-progress"
                                    ? "bg-[#CCDDF380] text-b-55"
                                    : item?.status === "pending"
                                    ? "bg-[#FCC34E26] text-[#F4B000]"
                                    : item?.status === "cancelled"
                                    ? "bg-[#E4281D12] text-[#E4281D]"
                                    : "bg-[#CCDDF380] text-b-55"
                            } text-xs py-[2px] px-2 rounded-2xl capitalize`}
                        >
                            {item?.status}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
        ];
    });

    const tableHeader = [
        { title: "Order Id", widthClass: "w-[250px] ", titleClass: "text-slate-700" },
        { title: "Customer name", widthClass: "w-[145px]", titleClass: "text-slate-700" },
        { title: "Order date", widthClass: "w-[112px]", titleClass: "text-slate-700" },
        { title: "Shipping address", widthClass: "w-[278px]", titleClass: "text-slate-700" },
        { title: "Status", widthClass: "w-[148px]", titleClass: "text-slate-700" },
        { title: "Total value", widthClass: "w-[106px]", titleClass: "text-slate-700" },
    ];

    const tableBody = resultMessage?.orders?.map((item, idx) => {
        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">SO-{item?.taId || item?.orderId || item?._id}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">
                            {item?.customerDetails?.first_name || item?.customerDetails?.last_name
                                ? `${item?.customerDetails?.first_name || ""} ${
                                      item?.customerDetails?.last_name || ""
                                  }`
                                : item?.customerDetails?.name || ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">
                            {item?.saleDate
                                ? moment(item?.saleDate)?.format("MMM DD,YYYY")
                                : "Not Available"}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">{`${item?.customerDetails?.address1 || ""} ${
                            item?.customerDetails?.address2
                                ? `, ${item?.customerDetails?.address2}`
                                : ""
                        }${item?.customerDetails?.city ? `, ${item?.customerDetails?.city}` : ""}${
                            item?.customerDetails?.country
                                ? `, ${item?.customerDetails?.country}`
                                : ""
                        }`}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex items-center justify-center gap-2`}
                    >
                        <p
                            className={`${
                                item?.status === "fulfilled"
                                    ? "bg-[#D0F4ED] text-g-60"
                                    : item?.status === "in-progress"
                                    ? "bg-[#CCDDF380] text-b-55"
                                    : item?.status === "pending"
                                    ? "bg-[#FCC34E26] text-[#F4B000]"
                                    : item?.status === "cancelled"
                                    ? "bg-[#E4281D12] text-[#E4281D]"
                                    : "bg-[#CCDDF380] text-b-55"
                            } text-xs py-[2px] px-2 rounded-2xl capitalize`}
                        >
                            {item?.status}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">
                            {" "}
                            {getCurrencyFromCurrencyCode(item?.currency)}
                            {formatMoney()?.format(item?.totalRevenue)}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
        ];
    });

    const updateOrderTableHeader = [
        { title: "Order Id", widthClass: "w-[250px] ", titleClass: "text-slate-700" },
        { title: "Product name", widthClass: "w-[177px]", titleClass: "text-slate-700" },
        { title: "Previous status", widthClass: "w-[177px]", titleClass: "text-slate-700" },
        { title: "Updated status", widthClass: "w-[177px]", titleClass: "text-slate-700" },
        { title: "Order date", widthClass: "w-[177px]", titleClass: "text-slate-700" },
    ];

    const updateOrderTableBody = resultMessage?.orders?.map((item, idx) => {
        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">SO-{item?.taId || item?.orderId || item?._id}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium `}
                    >
                        <p className="">
                            {item?.products?.map((item: any) => item?.productName).join(", ")}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex items-center justify-center gap-2`}
                    >
                        <p
                            className={`${
                                item?.previousStatus === "fulfilled"
                                    ? "bg-[#D0F4ED] text-g-60"
                                    : item?.previousStatus === "in-progress"
                                    ? "bg-[#CCDDF380] text-b-55"
                                    : item?.previousStatus === "pending"
                                    ? "bg-[#FCC34E26] text-[#F4B000]"
                                    : item?.previousStatus === "cancelled"
                                    ? "bg-[#E4281D12] text-[#E4281D]"
                                    : "bg-[#CCDDF380] text-b-55"
                            } text-xs py-[2px] px-2 rounded-2xl`}
                        >
                            {item?.previousStatus}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex items-center justify-center gap-2`}
                    >
                        <p
                            className={`${
                                item?.status === "fulfilled"
                                    ? "bg-[#D0F4ED] text-g-60"
                                    : item?.status === "in-progress"
                                    ? "bg-[#CCDDF380] text-b-55"
                                    : item?.status === "pending"
                                    ? "bg-[#FCC34E26] text-[#F4B000]"
                                    : item?.status === "cancelled"
                                    ? "bg-[#E4281D12] text-[#E4281D]"
                                    : "bg-[#CCDDF380] text-b-55"
                            } text-xs py-[2px] px-2 rounded-2xl capitalize`}
                        >
                            {item?.status}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-slate-600 font-rocGroteskMedium flex gap-2`}
                    >
                        <p className="">
                            {item?.saleDate
                                ? moment(item?.saleDate)?.format("DD.MM.YYYY; H:mm a")
                                : "Not Available"}
                        </p>
                    </div>
                ),
                widthClass: "!border-b",
            },
        ];
    });

    return (
        <div>
            <div className="overflow-x-clip">
                {toolName === "listSalesOrders" &&
                    resultMessage?.orders?.length > 0 &&
                    (Object.keys(resultMessage?.filters)?.length > 0 &&
                    Object.keys(resultMessage?.filters)?.includes("address") ? (
                        <CustomTable
                            tableBody={tableBody}
                            tableHeader={tableHeader}
                            isCellBordered={true}
                            stickyHeader={true}
                            headerContainerClass="!bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium "
                            tableClass="mb-4"
                            isScrollable={true}
                            scrollWithin={true}
                        />
                    ) : (
                        <CustomTable
                            tableBody={salesOrderTableBody}
                            tableHeader={salesOrderTableHeader}
                            isCellBordered={true}
                            stickyHeader={true}
                            headerContainerClass="!bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium "
                            tableClass="mb-4"
                            isScrollable={true}
                            scrollWithin={true}
                        />
                    ))}

                <div className="flex justify-end mt-5">
                    <ResponsivePaginationComponent
                        current={page}
                        total={resultMessage?.pagination?.number_of_pages}
                        maxWidth={350}
                        onPageChange={(page) => {
                            setPage(page);
                        }}
                    />
                </div>

                {toolName === "updateOrderStatus" && resultMessage?.orders?.length > 0 && (
                    <CustomTable
                        tableBody={updateOrderTableBody}
                        tableHeader={updateOrderTableHeader}
                        isCellBordered={true}
                        stickyHeader={true}
                        headerContainerClass="!bg-slate-50 "
                        headerItemClass="font-rocGroteskMedium "
                        tableClass="mb-4"
                        isScrollable={true}
                        scrollWithin={true}
                    />
                )}
            </div>
        </div>
    );
};

export default SalesOrderTable;
